import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Calendar, DollarSign, Clock, CheckCircle, XCircle, Building2 } from "lucide-react";

interface BookingWithEvent {
  id: string;
  company_name: string;
  sponsorship_interest: string;
  selected_package: string | null;
  package_price: number | null;
  status: string;
  created_at: string;
  organizer_notes: string | null;
  event: {
    id: string;
    name: string;
    image: string | null;
    category: string;
  };
}

export const MySponsorshipBookings = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<BookingWithEvent[]>([]);

  const fetchBookings = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch user's sponsorship bookings
      const { data: bookingsData, error } = await supabase
        .from('sponsorship_bookings')
        .select(`
          id,
          company_name,
          sponsorship_interest,
          selected_package,
          package_price,
          status,
          created_at,
          organizer_notes,
          event_id
        `)
        .eq('requester_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!bookingsData || bookingsData.length === 0) {
        setBookings([]);
        return;
      }

      // Fetch event details
      const eventIds = [...new Set(bookingsData.map(b => b.event_id))];
      const { data: eventsData } = await supabase
        .from('event_organizers')
        .select('id, name, image, category')
        .in('id', eventIds);

      // Combine data
      const combinedBookings: BookingWithEvent[] = bookingsData.map(booking => {
        const event = eventsData?.find(e => e.id === booking.event_id);
        return {
          ...booking,
          event: event || { id: booking.event_id, name: 'Unknown', image: null, category: 'Unknown' },
        };
      });

      setBookings(combinedBookings);
    } catch (error) {
      console.error('Error fetching sponsorship bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [user]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'accepted':
        return (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            {isRTL ? "مقبول" : "Accepted"}
          </Badge>
        );
      case 'completed':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            {isRTL ? "مكتمل" : "Completed"}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="h-3 w-3 mr-1" />
            {isRTL ? "مرفوض" : "Rejected"}
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="secondary">
            {isRTL ? "ملغى" : "Cancelled"}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <Clock className="h-3 w-3 mr-1" />
            {isRTL ? "قيد الانتظار" : "Pending"}
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6 mb-8">
      <h2 className="text-xl font-semibold">
        {isRTL ? "طلبات الرعاية" : "My Sponsorship Requests"}
      </h2>

      <div className="grid gap-4">
        {bookings.map((booking) => (
          <Card key={booking.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Event Info */}
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={booking.event.image || undefined} />
                    <AvatarFallback>
                      <Building2 className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <h4 className="font-semibold truncate">{booking.event.name}</h4>
                    <p className="text-sm text-muted-foreground capitalize">
                      {booking.event.category}
                    </p>
                  </div>
                </div>

                {/* Booking Details */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {booking.selected_package && (
                      <Badge variant="outline">{booking.selected_package}</Badge>
                    )}
                    {getStatusBadge(booking.status)}
                  </div>

                  <p className="text-sm text-muted-foreground">
                    {isRTL ? "الشركة: " : "Company: "}{booking.company_name}
                  </p>

                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {booking.package_price && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {booking.package_price.toLocaleString()} SAR
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(booking.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {booking.sponsorship_interest && (
                    <p className="text-sm text-muted-foreground">
                      {isRTL ? "الاهتمام: " : "Interest: "}{booking.sponsorship_interest}
                    </p>
                  )}

                  {booking.organizer_notes && (
                    <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                      {isRTL ? "ملاحظات المنظم: " : "Organizer notes: "}
                      {booking.organizer_notes}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
